import { toast } from "react-toastify";
import {
  startLoading,
  stopLoading,
  hasError,
  handleCountryStateDetails,
  handleCountryGridDetails,
} from "../redux/slices/countryApiSlice.ts";
import { method, paths } from "./ServiceConstants.tsx";
import ServicesManagerAPICall from "./ServicesManagerAPICall.tsx";
import { handleAuthentication } from "../redux/slices/loginApiSlice.ts";

const L2_KEY_TO_LABEL: Record<string, string> = {
  rejected: "Rejected",
  repudiated: "Repudiated",
  cwp: "CWP",
  payment_not_initiated: "Payment Not Initiated",
};

const toIso = (val?: string) => {
  if (!val) return val;
  if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
  const m = /^(\d{2})-(\d{2})-(\d{4})$/.exec(val);
  if (m) {
    const [, dd, mm, yyyy] = m;
    return `${yyyy}-${mm}-${dd}`;
  }
  return val;
};

const toTokens = (l2: any): string[] =>
  (Array.isArray(l2) ? l2.join(" ") : String(l2 ?? ""))
    .toLowerCase()
    .split(/[,\s/&|+]+/)
    .filter(Boolean);

const toLevel2Label = (l2: any): string | undefined => {
  const toks = toTokens(l2);
  if (!toks.length) return undefined;
  for (const t of toks) if (L2_KEY_TO_LABEL[t]) return L2_KEY_TO_LABEL[t];
  const first = toks[0];
  return first.charAt(0).toUpperCase() + first.slice(1);
};

const L2_KEYS = new Set(Object.keys(L2_KEY_TO_LABEL));

const normalizeFiltersParams = (raw: any) => {
  const p = { ...(raw || {}) };

  // Clean level3 accidental quotes (fix All Crops%27 issue)
  if (typeof p.level3 === "string") p.level3 = p.level3.replace(/['"]/g, "");

  // Convert dates to ISO
  if (p.startdate) p.startdate = toIso(p.startdate);
  if (p.enddate) p.enddate = toIso(p.enddate);

  // Normalize these L2 keys if used
  if (
    (p.level1 === "Claims" || p.level1 === "claims") &&
    typeof p.level2 === "string"
  ) {
    const v = String(p.level2);
    if (L2_KEYS.has(v)) p.level2 = L2_KEY_TO_LABEL[v]; // key -> label
  }

  return p;
};
/* ------------------------------------------------------------------- */

export const checkTokenMessage = (res: any) => {
  const message = res?.message ? res?.message.toLowerCase() : "";
  return (
    message.includes("invalid") ||
    message.includes("token has expired") ||
    message === "token is missing!"
  );
};

export const fetchCountryStateWiseDetails = (data) => {
  return async (dispatch: any) => {
    dispatch(startLoading());
    try {
      const normalized = normalizeFiltersParams(data);

      const tableData = await ServicesManagerAPICall.request(
        method.GET,
        paths.STATE_VIEW_DATA_V2,
        normalized,
        true,
        false,
      ).then(
        (tableData: any) => {
          if (tableData != null) {
            if (checkTokenMessage(tableData)) {
              dispatch(handleAuthentication({}));
            } else {
              // Attach meta so reducer can compute the country table rows
              dispatch(
                handleCountryStateDetails({
                  ...tableData,
                  __level1: normalized?.level1,
                  __level2: normalized?.level2,
                  __view: "country",
                }),
              );
            }
          } else {
            toast("Unable to fetch data, please try again later");
          }
        },
        (_err: any) => {
          dispatch(stopLoading());
          toast("Unable to fetch data, please try again later");
        },
      );
    } catch (error: any) {
      dispatch(stopLoading());
      toast("Unable to fetch data, please try again later");
      dispatch(hasError(error.toString()));
    }
  };
};

export const fetchCountryGridWiseDetails = (params) => {
  return async (dispatch: any) => {
    dispatch(startLoading());
    try {
      const normalized = normalizeFiltersParams(params);

      const styleKeys = ["premium", "suminsured", "policies"];
      if (!styleKeys.some((k) => normalized?.[k] === true)) {
        normalized.premium = true;
      }

      const tableData = await ServicesManagerAPICall.request(
        method.GET,
        paths.STATE_VIEW_DATA_V2,
        normalized,
        true,
        false,
      );

      if (!tableData) {
        dispatch(stopLoading());
        toast("Unable to fetch data, please try again later");
        return;
      }
      if (checkTokenMessage(tableData)) {
        dispatch(handleAuthentication({}));
        return;
      }

      dispatch(
        handleCountryGridDetails({
          ...tableData,
          __level1: normalized?.level1,
          __level2: normalized?.level2,
          __view: "grid",
        }),
      );
    } catch (error: any) {
      dispatch(stopLoading());
      toast("Unable to fetch data, please try again later");
      dispatch(hasError(error.toString()));
    }
  };
};
